import React, { useMemo } from 'react'
import { CollaborationTask as CollaborationTaskType } from '../../types/collaborations/CollaborationTaskTypes'
import { Link } from 'react-router-dom';
import { copyToClipboard, maskString } from '../../utils/helpers';
import moment from 'moment';
const CollaborationTask: React.FC<{ collaborationTask: CollaborationTaskType }> = ({ collaborationTask }) => {

    const { myStep, agencySteps, hubStep } = useMemo(() => {
        const activeSteps = Array.isArray(collaborationTask?.active_steps) ? collaborationTask.active_steps : [];
        const myStep = activeSteps.find((step) => step.assignees?.includes('influencer'));
        const agencySteps = activeSteps.find((step) => step.assignees?.includes('agency'));
        const hubStep = activeSteps.find((step) => step.assignees?.includes('hub'));
        return { myStep, agencySteps, hubStep };
    }, [collaborationTask])

    const isCompleted = useMemo(() => {
        if (!collaborationTask.active_milestone) {
            const completedMilestones = (collaborationTask.milestones ?? []).filter((m) => m.ended_at);
            return completedMilestones.length === (collaborationTask.milestones ?? []).length;
        }
        return false;
    }, [collaborationTask])

    const lastMilestone = useMemo(() => {
        if(!collaborationTask) return null;
        const milestones = [...(collaborationTask.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order);
        if(milestones.length){
            return milestones[milestones.length -1];
        }
        return null;
    }, [collaborationTask])

    const payments = useMemo(() => {
        return Array.isArray(lastMilestone?.payments) ? lastMilestone?.payments : [];
    }, [lastMilestone])


    const getPlannedPayoutDate = () => {
        if (!lastMilestone?.planned_payout_date) return "-";
        const momentObj = moment(lastMilestone.planned_payout_date);
        const isToday = momentObj.isSame(moment(), 'day');
        if (isToday) return "today";
        const isTomorrow = momentObj.isSame(moment().add(1, 'day'), 'day');
        if (isTomorrow) return "tomorrow";

        return "on " + momentObj.format("MMMM Do YYYY");

    }

    const pendingPayoutAmount = useMemo(() => {
        if(!payments) return 0;
        const amount = payments.reduce((sum, payment) => {
            if (payment.status === 'pending') {
                return sum + Number(payment.amount);
            }
            return sum;
        }, 0);
        return amount;
    }, [payments]) 

    return (
        <div className='d-flex flex-column'>
            <Link to={{ pathname: isCompleted ? `/collaborations/${collaborationTask?.campaign?.uuid}` : `/collaborations/${collaborationTask?.campaign?.uuid}/milestone/${collaborationTask?.active_milestone?.id}` }} className="milestone-card">
                <div className="d-flex gap-2">
                    <div className='bg-image bgc-content w-25' style={{ backgroundImage: `url(${collaborationTask.campaign.hero_img_url})`, height: '68px', borderRadius: '8px' }}></div>
                    <div className="d-flex flex-column w-75 justify-content-between gap-2">
                        <div className='d-flex flex-column gap-1'>

                            <h6 className='w-100 text-truncate m-0'>{collaborationTask.campaign?.name}</h6>
                            <span className="text-muted lh-1">{collaborationTask.active_milestone?.gig_type?.name}</span>
                            {
                                isCompleted && (collaborationTask.milestones ?? []).length > 0 &&
                                <span style={{ fontSize: '12px' }} className="text-muted lh-1">
                                    {collaborationTask.milestones[0].gig_type?.name} {(collaborationTask.milestones.length - 1) > 0 && <span className="">+{(collaborationTask.milestones.length - 1)} more </span>}
                                </span>
                            }
                        </div>

                        {
                            myStep ? (
                                <span style={{ fontSize: '12px' }} className='lh-1 text-success'>
                                    <span className="fw-bold">Action Needed: </span>
                                    <span>{myStep.gig_step.title ?? myStep.gig_step.name}</span>
                                </span>
                            )
                                : (
                                    agencySteps ? (
                                        <span style={{ fontSize: '12px' }} className='lh-1 text-warning'>
                                            <span className="fw-bold">Waiting for: </span>
                                            <span>{agencySteps.gig_step?.non_assignee_title ?? (agencySteps.gig_step.title ?? agencySteps.gig_step.name)}</span>
                                        </span>
                                    ) : (
                                        hubStep ? (
                                            <span style={{ fontSize: '12px' }} className='lh-1 text-muted'>
                                                <span className="fw-bold">Waiting for: </span>
                                                <span>{(hubStep.gig_step.title ?? hubStep.gig_step.name)}</span>
                                            </span>
                                        ) : ''
                                    )
                                )
                        }
                        {
                            isCompleted && <span style={{ fontSize: '12px' }} className='lh-1 text-muted fw-bold'>Completed</span>
                        }
                    </div>

                </div>
            </Link>
            {
                collaborationTask.hub && !isCompleted &&
                <div className='collab-task-hub'>
                    <div className="collab-task-hub-inner">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex flex-column gap-1">
                                <span className="hub-contact">
                                    {collaborationTask.hub.name} | {maskString(collaborationTask.hub.mobile ?? '', 3,7)} <button className='unset-all' onClick={() => copyToClipboard(collaborationTask.hub?.mobile ?? "", "Contact number copied to your clipboard!")}><i className="ms-1 far fa-copy text-primary"></i></button>
                                </span>
                                <span className="hub-heading">
                                    Campaign Manager
                                </span>
                            </div>
                            <a href={`https://api.whatsapp.com/send?phone=${collaborationTask.hub.mobile}&text=Hi`} rel="noreferrer" target="_blank" className='text-success fs-5'><i className='fab fa-whatsapp'></i></a>
                        </div>
                    </div>
                </div>
            }
            {
                isCompleted && (pendingPayoutAmount > 0) &&
                <div className='collab-task-hub'>
                    <div className="collab-task-hub-inner">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex flex-column gap-1">
                                <span className="hub-heading">
                                Your payment of ₹{pendingPayoutAmount} will be paid {getPlannedPayoutDate()}.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default CollaborationTask